import type { BadgeTone } from "components/ui/StatusBadge";

/**
 * @param {string} status work status value
 * @return {object} label と StatusBadge tone
 */
export const getWorkStatusBadge = (
  status: string,
): { label: string; tone: BadgeTone } => {
  switch (status) {
    case "working":
      return { label: "稼働中", tone: "soft-success" };
    case "ending_soon":
      return { label: "もうすぐ空き", tone: "soft-warning" };
    default:
      return { label: "空き", tone: "soft-info" };
  }
};

/**
 * @param {string} affiliation affiliation value
 * @return {string} display label
 */
export const getAffiliationLabel = (affiliation: string): string => {
  switch (affiliation) {
    case "proprietary":
      return "自社";
    case "partner":
      return "パートナー";
    default:
      return affiliation;
  }
};

/**
 * @param {string} type employment type value
 * @return {string} display label
 */
export const getEmploymentTypeLabel = (type: string): string => {
  switch (type) {
    case "full_time":
      return "正社員";
    case "freelancer":
      return "フリーランス";
    case "contract":
      return "契約社員";
    default:
      return type;
  }
};

/**
 * @param {string} pref remote preference value
 * @return {string} display label
 */
export const getRemotePreferenceLabel = (
  pref: string | null | undefined,
): string => {
  switch (pref) {
    case "full_remote":
      return "フルリモート";
    case "hybrid":
      return "ハイブリッド";
    case "onsite_only":
      return "出社";
    default:
      return "-";
  }
};

/**
 * @param {string} stage pipeline stage value
 * @return {BadgeTone} stage tone
 */
export const getStageTone = (stage: string): BadgeTone => {
  const found = PIPELINE_STAGES.find(s => s.value === stage);
  return found?.tone ?? "soft-neutral";
};

/**
 * 雇用形態（DB値）→ HRタイプ（プリセット/メール解析値）のマッピング
 * DB: full_time, freelancer, contract
 * HR: fullTimeEmployee, freelancer, contractEmployee
 */
export const EMPLOYMENT_TYPE_TO_HR_TYPE: Record<string, string> = {
  full_time: "fullTimeEmployee",
  freelancer: "freelancer",
  contract: "contractEmployee",
};

/**
 * HRタイプ（プリセット/メール解析値）→ 雇用形態（DB値）のマッピング
 */
export const HR_TYPE_TO_EMPLOYMENT_TYPE: Record<string, string> = {
  fullTimeEmployee: "full_time",
  contractEmployee: "contract",
  freelancer: "freelancer",
};

/**
 * @param {string} stage pipeline stage value
 * @return {string} display label
 */
export const getStageLabel = (stage: string): string => {
  switch (stage) {
    case "preparation":
      return "提案準備";
    case "proposed":
      return "提案済";
    case "doc_screening":
      return "書類通過";
    case "interview":
      return "面談中";
    case "offer":
      return "内定";
    case "won":
      return "成約";
    case "lost":
      return "見送り";
    default:
      return stage;
  }
};

// ================================================================
// 営業進捗ステージ (順序のある状態 → stage-* tone)
// ================================================================

export const PIPELINE_STAGES: ReadonlyArray<{
  value: string;
  label: string;
  tone: BadgeTone;
}> = [
  { value: "preparation", label: "提案準備", tone: "stage-1" },
  { value: "proposed", label: "提案済", tone: "stage-2" },
  { value: "doc_screening", label: "書類選考", tone: "stage-3" },
  { value: "interview", label: "面談", tone: "stage-4" },
  { value: "offer", label: "オファー", tone: "stage-final" },
  { value: "won", label: "成約", tone: "critical-success" },
  { value: "lost", label: "失注", tone: "soft-neutral" },
] as const;

// ================================================================
// 案件ステータス
// ================================================================

export const FOCUS_MATTER_STATUSES: ReadonlyArray<{
  value: string;
  label: string;
  tone: BadgeTone;
}> = [
  { value: "open", label: "募集中", tone: "soft-info" },
  { value: "filling", label: "候補あり", tone: "soft-warning" },
  { value: "filled", label: "充足", tone: "soft-success" },
  { value: "closed", label: "クローズ", tone: "soft-neutral" },
] as const;

/**
 * 案件ステータスのラベルと tone を返す
 * @param {string} status ステータス値
 * @return {object} label, tone
 */
export const getFocusMatterStatusBadge = (
  status: string,
): { label: string; tone: BadgeTone } => {
  const found = FOCUS_MATTER_STATUSES.find(s => s.value === status);
  return found ?? { label: status, tone: "soft-neutral" };
};

// ================================================================
// 契約ステータス
// ================================================================

/**
 * 契約ステータスのラベルと tone を返す
 * @param {string} status 契約ステータス値
 * @return {object} label, tone
 */
export const getContractStatusBadge = (
  status: string,
): { label: string; tone: BadgeTone } => {
  switch (status) {
    case "active":
      return { label: "稼働中", tone: "soft-success" };
    case "renewal_pending":
      return { label: "更新待ち", tone: "soft-warning" };
    case "ended":
      return { label: "終了", tone: "soft-neutral" };
    case "renewed":
      return { label: "更新済", tone: "soft-success" };
    default:
      return { label: status, tone: "soft-neutral" };
  }
};

// ================================================================
// 金額フォーマット
// ================================================================

/**
 * 円単位の金額を万円表示にフォーマットする
 * 端数がある場合は小数点1桁まで表示する
 * @param {number} yen 円単位の金額
 * @return {string} 万円表示の文字列（例: "68万円", "68.5万円"）
 */
export const formatYenToMan = (yen: number): string => {
  const man = yen / 10000;
  if (Number.isInteger(man)) {
    return `${man.toLocaleString()}万円`;
  }
  return `${man.toLocaleString(undefined, { maximumFractionDigits: 1 })}万円`;
};

/**
 * 円単位の金額を万の数値部分のみ返す（単位なし）
 * @param {number} yen 円単位の金額
 * @return {string} 万単位の数値文字列（例: "68", "68.5"）
 */
export const formatYenToManNum = (yen: number): string => {
  const man = yen / 10000;
  if (Number.isInteger(man)) {
    return man.toLocaleString();
  }
  return man.toLocaleString(undefined, { maximumFractionDigits: 1 });
};
